import { ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DestroyRef, inject, Injectable, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge, Observable } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { MobilePopUpComponent } from './mobile-pop-up.component';

@Injectable({
  providedIn: 'root',
})
export class MobilePopUpService {
  private readonly _configurableFocusTrapFactory = inject(ConfigurableFocusTrapFactory);
  private readonly _overlay: Overlay = inject(Overlay);
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  isPopUpOpen = false;

  private _popUp: MobilePopUpComponent;
  private _overlayRef: OverlayRef;
  private _viewContainerRef: ViewContainerRef;

  get overlayFirstChildEl(): Element {
    return this._overlayRef.overlayElement.children.item(0)!;
  }

  setupPopUp(popup: MobilePopUpComponent, viewContainerRef: ViewContainerRef): void {
    this._popUp = popup;
    this._viewContainerRef = viewContainerRef;
  }

  open(): void {
    this.isPopUpOpen = true;

    this._createPopUpOverlay();
    this._attachPopUpComponent();

    // Create focus trap for the popup
    this._configurableFocusTrapFactory.create(this._overlayRef.overlayElement);

    // Wait for the element to be drawn then add class
    requestAnimationFrame(() => {
      this.overlayFirstChildEl.classList.add('move');
    });

    // start listening to close popup actions
    this._popUpClosingActions()
      .pipe(takeUntilDestroyed(this._destroyRef), take(1))
      .subscribe(() => this._destroyPopUp());
  }

  close(): void {
    this._destroyPopUp();
  }

  private _destroyPopUp(): void {
    if (!this._overlayRef) {
      return;
    }

    // Wait 5s for transition end before detaching
    this._getElementMutationObservable(this.overlayFirstChildEl)
      .pipe(takeUntilDestroyed(this._destroyRef), delay(500), take(1))
      .subscribe(() => {
        this.isPopUpOpen = false;
        this._overlayRef.detach();
      });

    this.overlayFirstChildEl.classList.remove('move');
  }

  private _popUpClosingActions(): Observable<MouseEvent | void> {
    const backdropClick$ = this._overlayRef.backdropClick();
    const detachment$ = this._overlayRef.detachments();
    const popUpClosed$ = this._popUp.closed$;

    return merge(backdropClick$, detachment$, popUpClosed$);
  }

  private _createPopUpOverlay(): void {
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      positionStrategy: this._overlay.position().global().centerHorizontally().bottom(),
      panelClass: 'mobile-popup-menu',
    });
  }

  private _attachPopUpComponent(): void {
    const templatePortal = new TemplatePortal(this._popUp.templateRef, this._viewContainerRef);
    this._overlayRef.attach(templatePortal);
  }

  private _getElementMutationObservable(element: Element): Observable<MutationRecord[]> {
    return new Observable((observer) => {
      const mutation = new MutationObserver((mutations) => {
        observer.next(mutations);
      });

      mutation.observe(element, { attributes: true });

      return () => {
        mutation.disconnect();
      };
    });
  }
}
